import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  Appointment,
  User,
  Service,
  QuestionnaireSubmission,
  QuestionnaireTemplate,
  MedicalCertificate,
  Venue,
  SystemConfig,
  Question
} from '@/types';
import { showAlertModal } from '@shared/components/common/AlertModal';
import {
  Clock,
  CheckSquare,
  FileText,
  UserX,
  AlertCircle,
  X,
  Award,
  Stethoscope,
  TestTube,
  PenTool,
  Eraser,
  Loader2,
  ChevronRight,
  MapPin,
  Calendar,
  ScrollText,
  ClipboardCheck,
  MessageSquareQuote
} from 'lucide-react';
import { api } from '@/services/api';
import { createId } from '@/lib/ids';
import { formatUkDateTime } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { buildMapById } from '@/lib/collections';

const CERTIFICATE_OUTCOMES = [
  {
    id: 'fit',
    label: 'Fit for Duties',
    bg: 'bg-green-50',
    border: 'border-green-500',
    text: 'text-green-700',
    hover: 'hover:bg-green-50'
  },
  {
    id: 'fit_restrictions',
    label: 'Fit with Restrictions',
    bg: 'bg-yellow-50',
    border: 'border-yellow-500',
    text: 'text-yellow-700',
    hover: 'hover:bg-yellow-50'
  },
  {
    id: 'referred',
    label: 'Referral Required',
    bg: 'bg-blue-50',
    border: 'border-blue-500',
    text: 'text-blue-700',
    hover: 'hover:bg-blue-50'
  },
  {
    id: 'unfit',
    label: 'Unfit for Work',
    bg: 'bg-red-50',
    border: 'border-red-500',
    text: 'text-red-700',
    hover: 'hover:bg-red-50'
  }
] as const;

interface TechnicianViewProps {
  currentUser: User;
  appointments: Appointment[];
  services: Service[];
  venues: Venue[];
  questionnaires: QuestionnaireTemplate[];
  submissions: QuestionnaireSubmission[];
  systemConfig?: SystemConfig;
  onUpdateStatus: (
    id: string,
    status: 'completed' | 'booked' | 'noshow' | 'cancelled',
    notify?: { email: boolean; sms: boolean }
  ) => void;
  onUpdateUser?: (user: User) => void;
  onIssueCertificate?: (cert: MedicalCertificate) => Promise<{ success: boolean; error?: string }>;
}

const SignatureCanvas = ({
  onSave,
  onCancel,
  initialDataUrl
}: {
  onSave: (dataUrl: string) => void;
  onCancel: () => void;
  initialDataUrl?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';
      }
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !initialDataUrl) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const dx = (canvas.width - drawWidth) / 2;
      const dy = (canvas.height - drawHeight) / 2;
      ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
    };
    img.src = initialDataUrl;
  }, [initialDataUrl]);

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x =
      'touches' in e
        ? e.touches[0].clientX - rect.left
        : (e as React.MouseEvent).nativeEvent.offsetX;
    const y =
      'touches' in e
        ? e.touches[0].clientY - rect.top
        : (e as React.MouseEvent).nativeEvent.offsetY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x =
      'touches' in e
        ? e.touches[0].clientX - rect.left
        : (e as React.MouseEvent).nativeEvent.offsetX;
    const y =
      'touches' in e
        ? e.touches[0].clientY - rect.top
        : (e as React.MouseEvent).nativeEvent.offsetY;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl mb-4 relative h-64 overflow-hidden shadow-inner">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={() => setIsDrawing(false)}
          onMouseLeave={() => setIsDrawing(false)}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={() => setIsDrawing(false)}
        />
        <div className="absolute top-2 right-2">
          <button
            onClick={clearCanvas}
            className="bg-white/80 p-2 rounded-full hover:bg-white shadow-sm text-slate-500"
          >
            <Eraser className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded font-bold"
        >
          Cancel
        </button>
        <button
          onClick={() => canvasRef.current && onSave(canvasRef.current.toDataURL())}
          className="bg-slate-900 text-white px-6 py-2 rounded-xl font-black hover:bg-slate-800 transition-all flex items-center gap-2 uppercase tracking-wide text-xs"
        >
          Save Signature
        </button>
      </div>
    </div>
  );
};

export const TechnicianView: React.FC<TechnicianViewProps> = ({
  currentUser,
  appointments,
  services,
  venues,
  questionnaires,
  submissions,
  systemConfig,
  onUpdateStatus,
  onUpdateUser,
  onIssueCertificate
}) => {
  const [selectedVenueFilter, setSelectedVenueFilter] = useState<string>('all');
  const [dateRangeFilter, setDateRangeFilter] = useState<'today' | 'tomorrow' | 'week'>('today');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'booked' | 'completed' | 'noshow' | 'cancelled'
  >('all');
  const [serviceFilter, setServiceFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [noShowConfirmId, setNoShowConfirmId] = useState<string | null>(null);
  const [notifyOptions, setNotifyOptions] = useState({ email: true, sms: true });
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isIssuing, setIsIssuing] = useState(false);
  const [issuingCertApp, setIssuingCertApp] = useState<Appointment | null>(null);
  const createDefaultCertData = (): Partial<MedicalCertificate> => ({
    outcome: 'fit',
    advice: ''
  });
  const [certData, setCertData] = useState<Partial<MedicalCertificate>>(createDefaultCertData);
  const [showBriefingOverlay, setShowBriefingOverlay] = useState(false);
  const servicesById = useMemo(() => buildMapById(services), [services]);
  const venuesById = useMemo(() => buildMapById(venues), [venues]);
  const questionnairesById = useMemo(() => buildMapById(questionnaires), [questionnaires]);

  useEffect(() => {
    setPage(1);
  }, [selectedVenueFilter, dateRangeFilter, statusFilter, serviceFilter, searchTerm, pageSize]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredAppointments = appointments
    .filter((a) => {
      if (currentUser.assignedVenueIds?.length && !currentUser.assignedVenueIds.includes(a.venueId))
        return false;
      if (selectedVenueFilter !== 'all' && a.venueId !== selectedVenueFilter) return false;
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (serviceFilter !== 'all' && a.serviceId !== serviceFilter) return false;
      if (normalizedSearch) {
        const haystack = `${a.customerName} ${a.customerEmail} ${a.id}`.toLowerCase();
        if (!haystack.includes(normalizedSearch)) return false;
      }
      const appDate = new Date(a.date).toDateString();
      const today = new Date().toDateString();
      const tomorrow = new Date(Date.now() + 86400000).toDateString();
      if (dateRangeFilter === 'today') return appDate === today;
      if (dateRangeFilter === 'tomorrow') return appDate === tomorrow;
      return true;
    })
    .sort((a, b) => a.time.localeCompare(b.time));

  const totalFiltered = filteredAppointments.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const pageStart = (page - 1) * pageSize;
  const pageEnd = pageStart + pageSize;
  const pagedAppointments = filteredAppointments.slice(pageStart, pageEnd);
  const pageRangeStart = totalFiltered === 0 ? 0 : pageStart + 1;
  const pageRangeEnd = Math.min(pageEnd, totalFiltered);
  const isStatusFilter = (
    value: string
  ): value is 'all' | 'booked' | 'completed' | 'noshow' | 'cancelled' => {
    return ['all', 'booked', 'completed', 'noshow', 'cancelled'].includes(value);
  };

  const handleIssueCertificate = async () => {
    if (!issuingCertApp) return;
    if (!currentUser.signature) {
      setIsSignatureModalOpen(true);
      return;
    }
    setIsIssuing(true);
    const today = new Date();
    const expiry = new Date();
    expiry.setFullYear(today.getFullYear() + 3);
    const certId = createId().toUpperCase();
    const newCertificate: MedicalCertificate = {
      id: certId,
      appointmentId: issuingCertApp.id,
      customerEmail: issuingCertApp.customerEmail,
      issuedAt: today.toISOString(),
      validUntil: expiry.toISOString(),
      technicianName: currentUser.name,
      technicianSignature: currentUser.signature,
      outcome: certData.outcome || 'fit',
      drugTestOutcome: certData.drugTestOutcome || undefined,
      advice: certData.advice,
      templateId: 'default'
    };
    try {
      if (onIssueCertificate) {
        const res = await onIssueCertificate(newCertificate);
        if (res.success) {
          setIssuingCertApp(null);
          setShowBriefingOverlay(false);
          setCertData(createDefaultCertData());
          showAlertModal('Certificate issued.');
        }
      }
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Error issuing certificate.'));
    } finally {
      setIsIssuing(false);
    }
  };
  const openCertificateRecord = (app: Appointment) => {
    setShowBriefingOverlay(false);
    setCertData(createDefaultCertData());
    setIssuingCertApp(app);
  };

  const grouped = pagedAppointments.reduce(
    (acc, a) => {
      const k = new Date(a.date).toDateString();
      if (!acc[k]) acc[k] = [];
      acc[k].push(a);
      return acc;
    },
    {} as Record<string, Appointment[]>
  );

  // Helper to find questionnaire answers
  const getSubmissions = (appId: string) =>
    submissions
      .filter((s) => s.appointmentId === appId)
      .sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());
  const getTemplate = (templateId?: string) =>
    templateId ? questionnairesById.get(templateId) : undefined;
  const QUESTION_COMMENT_SUFFIXES = new Set([
    'details',
    'detail',
    'comment',
    'comments',
    'commenttext',
    'detailstext',
    'note',
    'notes',
    'additionalinfo',
    'additionaldetails'
  ]);
  const getQuestionScopedSuffix = (key: string, questionId: string): string | null => {
    const normalizedKey = String(key || '')
      .trim()
      .toLowerCase();
    const normalizedQuestionId = String(questionId || '')
      .trim()
      .toLowerCase();
    if (
      !normalizedKey ||
      !normalizedQuestionId ||
      !normalizedKey.startsWith(normalizedQuestionId)
    ) {
      return null;
    }

    let suffix = normalizedKey.slice(normalizedQuestionId.length);
    if (!suffix) return '';
    if (suffix[0] === '_' || suffix[0] === '.' || suffix[0] === '-') {
      suffix = suffix.slice(1);
    }
    return suffix || null;
  };
  const normalizeAnswerValue = (value: unknown): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
      const nested = value as Record<string, unknown>;
      const preferredKeys = ['value', 'answer', 'response', 'selected', 'text'];
      for (const key of preferredKeys) {
        const candidate = nested[key];
        if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
      }
      return JSON.stringify(value);
    }
    return String(value);
  };
  const extractQuestionComment = (
    answers: Record<string, unknown>,
    questionId: string,
    rawAnswer: unknown
  ): string => {
    if (rawAnswer && typeof rawAnswer === 'object') {
      const nested = rawAnswer as Record<string, unknown>;
      const nestedKeys = [
        'details',
        'detail',
        'comment',
        'comments',
        'commentText',
        'detailsText',
        'note',
        'notes',
        'additionalInfo'
      ];
      for (const key of nestedKeys) {
        const nestedText = normalizeAnswerValue(nested[key]);
        if (nestedText) return nestedText;
      }
    }

    const preferredKeys = [
      `${questionId}_details`,
      `${questionId}_detail`,
      `${questionId}_comment`,
      `${questionId}_comments`,
      `${questionId}Details`,
      `${questionId}Detail`,
      `${questionId}Comment`,
      `${questionId}Comments`,
      `${questionId}_comment_text`,
      `${questionId}_details_text`,
      `${questionId}CommentText`,
      `${questionId}DetailsText`,
      `${questionId}_note`,
      `${questionId}_notes`,
      `${questionId}Note`,
      `${questionId}Notes`,
      `${questionId}_additional_info`,
      `${questionId}AdditionalInfo`
    ];
    const dynamicKeys = Object.keys(answers).filter((key) => {
      const suffix = getQuestionScopedSuffix(key, questionId);
      return Boolean(suffix && QUESTION_COMMENT_SUFFIXES.has(suffix));
    });
    const candidates = Array.from(new Set([...preferredKeys, ...dynamicKeys]));

    for (const key of candidates) {
      const text = normalizeAnswerValue(answers[key]);
      if (text) return text;
    }

    return '';
  };
  const isQuestionScopedKey = (key: string, questionId: string) =>
    String(key || '')
      .trim()
      .toLowerCase() ===
      String(questionId || '')
        .trim()
        .toLowerCase() || getQuestionScopedSuffix(key, questionId) !== null;
  const getUnmatchedAnswerEntries = (
    answers: Record<string, unknown>,
    questions: Question[]
  ): Array<[string, unknown]> => {
    const questionIds = new Set(questions.map((q) => q.id));
    return Object.entries(answers).filter(([key]) => {
      if (questionIds.has(key)) return false;
      return !questions.some((q) => isQuestionScopedKey(key, q.id));
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in">
      {/* Signature Modal */}
      {isSignatureModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scale-up overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center bg-slate-50">
              <h3 className="font-black text-slate-800 flex items-center gap-2 uppercase tracking-tight text-sm">
                <PenTool className="w-4 h-4 text-teal-600" /> Digital Signature Setup
              </h3>
              <button onClick={() => setIsSignatureModalOpen(false)}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-xs text-slate-500 mb-6 leading-relaxed">
                This signature will be appended to all clinical certificates you issue. Please
                ensure it is legible.
              </p>
              {currentUser.signature && (
                <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                    Current Signature
                  </p>
                  <div className="bg-white border border-dashed border-slate-200 rounded-lg p-3 flex items-center justify-center">
                    <img
                      src={currentUser.signature}
                      alt="Current signature"
                      className="max-h-24 w-auto object-contain"
                    />
                  </div>
                </div>
              )}
              <SignatureCanvas
                onSave={async (url) => {
                  try {
                    const res = await api.updateUserProfile({ ...currentUser, signature: url });
                    if (res.success && onUpdateUser) {
                      onUpdateUser({ ...currentUser, signature: url });
                      showAlertModal('Signature saved.');
                      setIsSignatureModalOpen(false);
                    }
                  } catch (err: unknown) {
                    showAlertModal(normalizeErrorMessage(err, 'Failed to update signature.'));
                  }
                }}
                onCancel={() => setIsSignatureModalOpen(false)}
                initialDataUrl={currentUser.signature}
              />
            </div>
          </div>
        </div>
      )}

      {/* Staff Briefing Overlay */}
      {showBriefingOverlay && issuingCertApp && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg animate-scale-up overflow-hidden border-4 border-amber-100">
            <div className="p-6 border-b bg-amber-50 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-amber-200 p-2 rounded-xl text-amber-700 shadow-inner">
                  <ScrollText className="w-5 h-5" />
                </div>
                <h3 className="font-black text-lg text-amber-900 uppercase tracking-tight">
                  Staff Briefing Notes
                </h3>
              </div>
              <button
                onClick={() => setShowBriefingOverlay(false)}
                className="bg-white p-1.5 rounded-full text-amber-400 hover:text-amber-600 shadow-sm"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-8 space-y-6">
              {issuingCertApp.staffPrivateNote ? (
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-amber-600 uppercase tracking-[0.2em]">
                    Booking Office Instructions:
                  </p>
                  <div className="bg-amber-50/50 p-4 rounded-2xl text-sm text-slate-700 italic border-l-4 border-amber-400 font-medium leading-relaxed">
                    "{issuingCertApp.staffPrivateNote}"
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">
                  No admin notes provided for this booking.
                </p>
              )}

              {issuingCertApp.technicianNotes && (
                <div className="space-y-2 pt-4 border-t border-slate-100">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                    Previous Clinical History:
                  </p>
                  <div className="bg-slate-50 p-4 rounded-2xl text-sm text-slate-600 italic border-l-4 border-slate-300 font-medium leading-relaxed">
                    "{issuingCertApp.technicianNotes}"
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 bg-slate-50 flex justify-center">
              <button
                onClick={() => setShowBriefingOverlay(false)}
                className="w-full bg-slate-900 text-white py-3 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10"
              >
                Return to Assessment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issuance Modal */}
      {issuingCertApp && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[90] p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col animate-scale-up border border-slate-200 overflow-hidden">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-teal-100 p-2.5 rounded-2xl text-teal-600 shadow-sm">
                  <Stethoscope className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-black text-xl text-slate-800 uppercase tracking-tight">
                    Clinical Assessment Record
                  </h3>
                  <div className="flex items-center gap-3">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                      PATIENT: {issuingCertApp.customerName} | Ref: {issuingCertApp.id}
                    </p>
                    {issuingCertApp.staffPrivateNote && (
                      <button
                        onClick={() => setShowBriefingOverlay(true)}
                        className="bg-amber-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest animate-pulse hover:bg-amber-600 transition-colors shadow-sm flex items-center gap-1"
                      >
                        <MessageSquareQuote className="w-3 h-3" /> Staff Note Available
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowBriefingOverlay(true)}
                  title="View Staff Notes"
                  className="hidden sm:flex items-center gap-2 text-[10px] font-black uppercase tracking-widest bg-white text-amber-600 border-2 border-amber-100 px-4 py-2 rounded-2xl hover:bg-amber-50 transition-all shadow-sm"
                >
                  <ScrollText className="w-4 h-4" /> Briefing
                </button>
                <button
                  onClick={() => setIssuingCertApp(null)}
                  disabled={isIssuing}
                  className="text-slate-400 hover:text-slate-700 bg-white p-2 rounded-full shadow-sm"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-8 overflow-y-auto flex-1 custom-scrollbar space-y-10">
              {/* Quick Briefing Button (Inline for Mobile/Ease of use) */}
              {(issuingCertApp.staffPrivateNote || issuingCertApp.technicianNotes) && (
                <button
                  onClick={() => setShowBriefingOverlay(true)}
                  className="w-full bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex items-center justify-between group hover:bg-amber-100 transition-all text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-white p-2 rounded-xl text-amber-600 shadow-sm group-hover:scale-110 transition-transform">
                      <ScrollText className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-black text-amber-800 text-[10px] uppercase tracking-[0.2em]">
                        Patient Briefing Required
                      </p>
                      <p className="text-xs text-amber-600 font-medium">
                        Click to view private staff notes and clinical history
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-amber-400" />
                </button>
              )}

              {/* Questionnaire Section */}
              <div className="border-t border-slate-100 pt-8">
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4 text-teal-600" /> Health Questionnaire
                  Responses
                </h4>
                {(() => {
                  const appSubmissions = getSubmissions(issuingCertApp.id);
                  const service = servicesById.get(issuingCertApp.serviceId);

                  if (appSubmissions.length === 0) {
                    return (
                      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-8 text-center">
                        <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500 font-medium italic">
                          No questionnaire responses found for this appointment.
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      {appSubmissions.map((submission) => {
                        const template =
                          getTemplate(submission.templateId) ||
                          (!submission.templateId
                            ? getTemplate(service?.questionnaireConfig?.templateId)
                            : undefined);

                        if (!template) {
                          const entries = Object.entries(submission.answers || {});
                          return (
                            <div
                              key={submission.id}
                              className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm"
                            >
                              <div className="bg-slate-50 px-4 py-2 border-b flex justify-between items-center">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                  Submitted Answers
                                </span>
                                <span className="text-[9px] text-slate-400">
                                  Completed: {formatUkDateTime(submission.submittedAt, '—')}
                                </span>
                              </div>
                              <div className="divide-y divide-slate-50">
                                {entries.length === 0 ? (
                                  <div className="p-4 text-center text-xs text-slate-500">
                                    No answers recorded.
                                  </div>
                                ) : (
                                  entries.map(([key, value]) => (
                                    <div
                                      key={key}
                                      className="p-4 hover:bg-slate-50/30 transition-colors"
                                    >
                                      <p className="text-xs font-bold text-slate-500 mb-1">{key}</p>
                                      <span className="text-xs text-slate-700 font-medium">
                                        {typeof value === 'string' ? value : JSON.stringify(value)}
                                      </span>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={submission.id}
                            className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm"
                          >
                            <div className="bg-slate-50 px-4 py-2 border-b flex justify-between items-center">
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                {template.name || 'Submitted Answers'}
                              </span>
                              <span className="text-[9px] text-slate-400">
                                Completed: {formatUkDateTime(submission.submittedAt, '—')}
                              </span>
                            </div>
                            <div className="divide-y divide-slate-50">
                              {(() => {
                                const answers = (submission.answers || {}) as Record<
                                  string,
                                  unknown
                                >;
                                const unmatchedEntries = getUnmatchedAnswerEntries(
                                  answers,
                                  template.questions
                                );

                                return (
                                  <>
                                    {template.questions.map((q) => {
                                      const rawAnswer = answers[q.id];
                                      const answerText = normalizeAnswerValue(rawAnswer);
                                      const commentText = extractQuestionComment(
                                        answers,
                                        q.id,
                                        rawAnswer
                                      );
                                      const yesNoTone =
                                        answerText.toLowerCase() === 'yes'
                                          ? 'bg-red-50 text-red-600'
                                          : answerText.toLowerCase() === 'no'
                                            ? 'bg-green-50 text-green-600'
                                            : 'bg-slate-100 text-slate-600';

                                      return (
                                        <div
                                          key={q.id}
                                          className="p-4 hover:bg-slate-50/30 transition-colors"
                                        >
                                          <p className="text-xs font-bold text-slate-500 mb-1">
                                            {q.label}
                                          </p>
                                          <div className="flex items-center gap-2">
                                            {q.type === 'yes_no' ? (
                                              <span
                                                className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${yesNoTone}`}
                                              >
                                                {answerText || 'No answer'}
                                              </span>
                                            ) : (
                                              <span className="text-xs text-slate-700 font-medium">
                                                {answerText || 'No answer'}
                                              </span>
                                            )}
                                          </div>
                                          {commentText && (
                                            <p className="mt-2 text-xs text-slate-700 font-medium">
                                              <span className="font-bold text-slate-500">
                                                Comment:
                                              </span>{' '}
                                              {commentText}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {unmatchedEntries.length > 0 && (
                                      <div className="p-4 bg-amber-50/40 border-t border-amber-100">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                                          Additional Response Data
                                        </p>
                                        <div className="mt-2 space-y-1">
                                          {unmatchedEntries.map(([key, value]) => (
                                            <p
                                              key={`${submission.id}-${key}`}
                                              className="text-xs text-slate-700"
                                            >
                                              <span className="font-bold text-slate-500">
                                                {key}:
                                              </span>{' '}
                                              {normalizeAnswerValue(value) || 'No answer'}
                                            </p>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Outcome Section */}
              <div className="border-t border-slate-100 pt-8">
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <Award className="w-4 h-4 text-teal-600" /> Clinical Conclusion
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {CERTIFICATE_OUTCOMES.map((opt) => (
                    <label
                      key={opt.id}
                      className={`border-2 rounded-2xl p-4 cursor-pointer transition-all flex flex-col items-center justify-center text-center gap-1.5 ${certData.outcome === opt.id ? `${opt.bg} ${opt.border} ring-4 ring-slate-900/5` : `border-slate-100 bg-white ${opt.hover}`}`}
                    >
                      <input
                        type="radio"
                        className="hidden"
                        checked={certData.outcome === opt.id}
                        onChange={() => setCertData({ ...certData, outcome: opt.id })}
                      />
                      <span
                        className={`font-black text-xs uppercase tracking-widest ${certData.outcome === opt.id ? opt.text : 'text-slate-500'}`}
                      >
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-100 pt-8">
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <TestTube className="w-4 h-4 text-teal-600" /> Drug Test Result
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { id: '', label: 'Not Recorded' },
                    { id: 'negative', label: 'Negative' },
                    { id: 'non_negative', label: 'Non-negative' },
                    { id: 'not_tested', label: 'Not Tested' }
                  ].map((option) => {
                    const isActive = String(certData.drugTestOutcome || '') === option.id;
                    return (
                      <label
                        key={option.id || 'empty'}
                        className={`border rounded-2xl p-3 cursor-pointer transition-all flex items-center justify-center text-center ${isActive ? 'bg-teal-50 border-teal-500 ring-2 ring-teal-100' : 'border-slate-200 bg-white hover:border-teal-300'}`}
                      >
                        <input
                          type="radio"
                          className="hidden"
                          checked={isActive}
                          onChange={() =>
                            setCertData({
                              ...certData,
                              drugTestOutcome:
                                (option.id as MedicalCertificate['drugTestOutcome']) || undefined
                            })
                          }
                        />
                        <span
                          className={`font-black text-[10px] uppercase tracking-widest ${isActive ? 'text-teal-700' : 'text-slate-500'}`}
                        >
                          {option.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="mt-2 text-[10px] text-slate-500">
                  Optional. Leave as "Not Recorded" to store no drug test result.
                </p>
              </div>

              <div>
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-teal-600" /> Clinical Advice & Restrictions
                </h4>
                <textarea
                  className="w-full border-2 border-slate-100 rounded-2xl p-4 text-sm h-32 focus:ring-4 focus:ring-teal-500/10 focus:border-teal-400 outline-none transition-all bg-slate-50 font-medium"
                  placeholder="Clinical notes for employer and patient..."
                  value={certData.advice}
                  onChange={(e) => setCertData({ ...certData, advice: e.target.value })}
                />
              </div>
            </div>

            <div className="p-6 border-t bg-slate-50 flex flex-col sm:flex-row justify-end gap-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setIssuingCertApp(null)}
                  disabled={isIssuing}
                  className="px-6 py-3 text-slate-400 font-bold uppercase text-[10px] tracking-widest"
                >
                  Discard
                </button>
                <button
                  onClick={handleIssueCertificate}
                  disabled={isIssuing}
                  className="bg-teal-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50 shadow-xl shadow-teal-600/20 active:scale-95 transition-all uppercase tracking-widest text-[11px]"
                >
                  {isIssuing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Award className="w-4 h-4" />
                  )}{' '}
                  Finalize & Issue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main UI Header */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <div>
          <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
            Clinician Dashboard
          </h2>
          <p className="text-slate-500 text-sm font-medium">
            Logged in as <span className="text-teal-600 font-black">{currentUser.name}</span>
          </p>
        </div>
        <button
          onClick={() => setIsSignatureModalOpen(true)}
          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-teal-600 bg-teal-50 hover:bg-teal-100 px-4 py-2.5 rounded-xl transition-all border border-teal-100"
        >
          {currentUser.signature ? 'Update Signature' : 'Setup Signature'}
        </button>
      </div>

      {/* Site Filters */}
      <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-3 mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[2fr_1fr_1fr_1fr] gap-3">
          <div className="flex bg-slate-50 p-1 rounded-xl w-full min-w-[260px]">
            <button
              onClick={() => setDateRangeFilter('today')}
              className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all whitespace-nowrap ${dateRangeFilter === 'today' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Today
            </button>
            <button
              onClick={() => setDateRangeFilter('tomorrow')}
              className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all whitespace-nowrap ${dateRangeFilter === 'tomorrow' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Tomorrow
            </button>
            <button
              onClick={() => setDateRangeFilter('week')}
              className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all whitespace-nowrap ${dateRangeFilter === 'week' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Full Rota
            </button>
          </div>
          <select
            className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-600 px-4 py-2.5 rounded-xl border-none outline-none focus:ring-0 cursor-pointer w-full"
            value={selectedVenueFilter}
            onChange={(e) => setSelectedVenueFilter(e.target.value)}
          >
            <option value="all">All Authorized Sites</option>
            {((venues as Venue[]) || [])
              .filter((v: Venue) => {
                const assigned = currentUser.assignedVenueIds;
                return !assigned || assigned.length === 0 || assigned.includes(v.id);
              })
              .map((v: Venue) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
          </select>
          <select
            className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-600 px-4 py-2.5 rounded-xl border-none outline-none focus:ring-0 cursor-pointer w-full"
            value={statusFilter}
            onChange={(e) => {
              if (isStatusFilter(e.target.value)) {
                setStatusFilter(e.target.value);
              }
            }}
          >
            <option value="all">All Statuses</option>
            <option value="booked">Booked</option>
            <option value="completed">Completed</option>
            <option value="noshow">No Show</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-600 px-4 py-2.5 rounded-xl border-none outline-none focus:ring-0 cursor-pointer w-full"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
          >
            <option value="all">All Services</option>
            {[...services]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-3">
          <input
            className="flex-1 bg-slate-50 text-xs font-semibold text-slate-700 placeholder:text-slate-400 px-4 py-2.5 rounded-xl border border-slate-100 outline-none focus:ring-2 focus:ring-teal-200"
            placeholder="Search name, email, or reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-600 px-4 py-2.5 rounded-xl border-none outline-none focus:ring-0 cursor-pointer w-full"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
      </div>

      <div className="space-y-12">
        {filteredAppointments.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200 shadow-sm flex flex-col items-center">
            <div className="bg-slate-50 p-4 rounded-full mb-4">
              <Calendar className="w-10 h-10 text-slate-200" />
            </div>
            <p className="text-slate-800 font-black uppercase tracking-widest text-sm">
              No appointments match your filters
            </p>
            <p className="text-slate-400 text-xs mt-2 max-w-[250px] mx-auto leading-relaxed">
              Adjust the date range or clear filters to see more appointments.
            </p>
          </div>
        ) : (
          (Object.entries(grouped) as [string, Appointment[]][]).map(([date, apps]) => (
            <div key={date} className="space-y-6">
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] flex items-center gap-3 px-2">
                <div className="h-px bg-slate-100 flex-1"></div>
                {date}
                <div className="h-px bg-slate-100 flex-1"></div>
              </h3>
              <div className="grid gap-4">
                {apps.map((app) => {
                  const svc = servicesById.get(app.serviceId);
                  const isDone = app.status === 'completed';
                  const isNoShow = app.status === 'noshow';
                  const isCancelled = app.status === 'cancelled';
                  const submissionCount = submissions.filter(
                    (s) => s.appointmentId === app.id
                  ).length;
                  const hasSubmission = submissionCount > 0;

                  return (
                    <div
                      key={app.id}
                      className={`bg-white rounded-3xl border p-6 flex flex-col sm:flex-row justify-between items-center gap-6 shadow-sm transition-all ${isDone || isNoShow || isCancelled ? 'opacity-50 border-slate-50' : 'border-slate-200 hover:border-teal-200 hover:shadow-md'}`}
                    >
                      <div className="flex items-center gap-6 flex-1">
                        <div className="bg-slate-50 border-2 border-slate-100 w-20 h-20 rounded-2xl flex items-center justify-center shrink-0 shadow-inner">
                          <Clock className="w-6 h-6 text-teal-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-black text-xl text-slate-900 leading-tight">
                              {app.customerName}
                            </h3>
                            {hasSubmission && (
                              <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border border-blue-100">
                                {submissionCount > 1 ? `${submissionCount} Forms` : 'Form Ready'}
                              </span>
                            )}
                            {app.staffPrivateNote && (
                              <span
                                className="bg-amber-50 text-amber-600 px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border border-amber-100"
                                title="Notes Available"
                              >
                                Notes
                              </span>
                            )}
                          </div>
                          <div className="text-teal-700 font-bold text-xs uppercase tracking-widest">
                            {svc?.name}
                          </div>
                          <div className="flex items-center gap-4 mt-3">
                            <span className="text-[9px] font-black uppercase text-slate-300 tracking-widest">
                              {app.time}
                            </span>
                            <span className="text-[9px] font-black uppercase text-slate-300 tracking-widest flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {venuesById.get(app.venueId)?.city}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {isDone ? (
                          <span className="flex items-center gap-2 text-green-600 font-black text-[10px] px-6 py-3 bg-green-50 rounded-2xl border border-green-100 uppercase tracking-widest">
                            <CheckSquare className="w-4 h-4" /> Certified
                          </span>
                        ) : isNoShow ? (
                          <span className="flex items-center gap-2 text-red-600 font-black text-[10px] px-6 py-3 bg-red-50 rounded-2xl border border-red-100 uppercase tracking-widest">
                            <UserX className="w-4 h-4" /> Missed
                          </span>
                        ) : isCancelled ? (
                          <span className="flex items-center gap-2 text-slate-600 font-black text-[10px] px-6 py-3 bg-slate-50 rounded-2xl border border-slate-100 uppercase tracking-widest">
                            <UserX className="w-4 h-4" /> Cancelled
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => setNoShowConfirmId(app.id)}
                              className="p-3 text-red-300 hover:text-red-500 transition-colors"
                            >
                              <UserX className="w-6 h-6" />
                            </button>
                            <button
                              onClick={() => openCertificateRecord(app)}
                              className="bg-slate-900 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10 active:scale-95"
                            >
                              Open Record
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {filteredAppointments.length > 0 && (
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-slate-500 font-semibold">
            Showing {pageRangeStart}-{pageRangeEnd} of {totalFiltered}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* No Show Modal */}
      {noShowConfirmId && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-sm w-full text-center animate-scale-up">
            <div className="bg-red-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <UserX className="w-10 h-10 text-red-600" />
            </div>
            <h3 className="font-black text-xl text-slate-900 uppercase tracking-tight mb-2">
              Mark as Missed?
            </h3>
            <p className="text-slate-500 text-xs mb-8 leading-relaxed font-medium">
              This will close the record as a 'No Show'. Automated alerts will be sent to the
              company.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setNoShowConfirmId(null)}
                className="py-3 px-6 text-slate-400 font-black uppercase text-[10px] tracking-widest"
              >
                Keep Open
              </button>
              <button
                onClick={() => {
                  onUpdateStatus(noShowConfirmId, 'noshow', notifyOptions);
                  setNoShowConfirmId(null);
                }}
                className="py-3 px-6 bg-red-600 text-white font-black uppercase text-[10px] tracking-widest rounded-2xl shadow-lg shadow-red-600/20"
              >
                Confirm No-Show
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
